import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { CircleSlash, X } from 'lucide-react';

import { DataTable } from 'components/DataTable';
import { Loader, LoaderModeType } from 'components/Loader';
import { EntityPageBreadcrumb, PageHeader } from 'components/PageHeader';
import { ActionBar } from 'components/ActionBar';
import { confirmPopup } from 'components/Popups/Confirm';
import {
    useGetSiteQuery,
    useGetColumnsQuery,
    useGetSiteProductsTreeQuery,
    useGetSiteProductsColumnStocksQuery,
    useCreateZeroColumnInventoryStocksMutation,
    GetColumnProductStocksDocument,
    GetColumnProductStocksQuery,
    GetColumnProductStocksQueryVariables,
    GetSiteProductColumnStocksDocument,
    GetSiteProductColumnStocksQuery,
    GetSiteProductColumnStocksQueryVariables,
    ProductState,
    ProductFlow,
    SiteProductsFilterInput,
    TotadminPermission,
} from 'data/__generated__';
import { PRODUCTS_STATES_LABELS } from 'pages/Products/constants/states';

import {
    getStockTreeColumns,
    columnToNode,
    productInColumnToNode,
    siteProductToNode,
    columnForProductToNode,
    buildColumnsWithMatchingProducts,
    parseColumnProductNodeId,
    type StockTreeNode,
    type VariationInfo,
} from './constants/stockTreeColumns';

type ParamTypes = {
    siteId: string;
};

type StockTreeView = 'columns' | 'products';
// Standalone product filters live in the table filterState (URL-synced); the faceted ones store string arrays.
type StockTreeFilter = { searchValue?: string; productName?: string; flow?: ProductFlow[]; states?: ProductState[] };

// Roots are loaded in one shot (no fetch-more); the DataTable virtualizes them.
const ROOTS_LIMIT = 100000;
const FILTER_DEBOUNCE_MS = 300;

const productStatesFilter = [ProductState.Regular, ProductState.Created];

const PRODUCT_FLOW_LABELS: Record<ProductFlow, string> = {
    [ProductFlow.Dry]: 'Sec',
    [ProductFlow.Fresh]: 'Frais',
    [ProductFlow.Fruit]: 'Fruits',
    [ProductFlow.Bakery]: 'Boulangerie',
    [ProductFlow.Caterer]: 'Traiteur',
    [ProductFlow.Kumo]: 'Kumo',
};
const FLOW_OPTIONS = Object.values(ProductFlow).map((flow) => ({ value: flow, label: PRODUCT_FLOW_LABELS[flow] }));
const STATE_OPTIONS = Object.values(ProductState).map((state) => ({
    value: state,
    label: PRODUCTS_STATES_LABELS[state] || state,
}));

const EXTRA_FILTERS = [
    { key: 'productName', title: 'Produit', type: 'text' as const, placeholder: 'Nom de produit…' },
    { key: 'flow', title: 'Flux', type: 'select' as const, options: FLOW_OPTIONS },
    { key: 'states', title: 'Statut', type: 'multi-select' as const, options: STATE_OPTIONS },
];

export const SiteStocksOverview = () => {
    const { siteId = '' } = useParams<ParamTypes>();
    const client = useApolloClient();
    const [view, setView] = useState<StockTreeView>('columns');
    const [infos, setInfos] = useState<VariationInfo | null>(null);

    // Server-backed product filters (productName / flow / states). Their values live in the DataTable filterState
    // (URL-synced) and reach us via onFiltersChange; we debounce them before driving the queries. They narrow the
    // products in BOTH views (product roots in products view; columns containing matching products in columns view).
    const [appliedFilters, setAppliedFilters] = useState<SiteProductsFilterInput>({ states: productStatesFilter });
    const [hasProductFilter, setHasProductFilter] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();
    const handleFiltersChange = useCallback((filters: StockTreeFilter) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            const productName = (filters.productName ?? '').trim();
            const flow = filters.flow && filters.flow.length > 0 ? filters.flow : undefined;
            const states = filters.states && filters.states.length > 0 ? filters.states : undefined;
            setHasProductFilter(Boolean(productName || flow || states));
            setAppliedFilters({
                states: states ?? productStatesFilter,
                ...(productName ? { searchValue: productName } : {}),
                ...(flow ? { flow } : {}),
            });
        }, FILTER_DEBOUNCE_MS);
    }, []);

    const { loading: siteLoading, data: siteData, error: siteError } = useGetSiteQuery({ variables: { siteId } });

    const {
        loading: columnsLoading,
        data: columnsData,
        error: columnsError,
    } = useGetColumnsQuery({ variables: { siteId } });

    // Products view roots (narrowed by the product filters when set).
    const {
        loading: productsLoading,
        data: productsData,
        error: productsError,
    } = useGetSiteProductsTreeQuery({
        variables: { siteId, offset: 0, limit: ROOTS_LIMIT, filters: appliedFilters },
        skip: !siteId || view !== 'products',
        fetchPolicy: 'cache-and-network',
    });

    // Columns view + active product filter: the matching products with their columns, grouped by column.
    const {
        loading: productColumnsLoading,
        data: productColumnsData,
        error: productColumnsError,
        refetch: refetchProductColumns,
    } = useGetSiteProductsColumnStocksQuery({
        variables: { siteId, limit: ROOTS_LIMIT, filters: appliedFilters },
        skip: !siteId || view !== 'columns' || !hasProductFilter,
        fetchPolicy: 'cache-and-network',
    });

    const [createZeroColumnInventoryStocks] = useCreateZeroColumnInventoryStocksMutation();

    // Selection of tree rows. Only enabled in the columns view + product filter, where the tree is fully
    // loaded (pre-expanded) and refetchable. Product-leaf node ids encode their (column, product) pair.
    const canSelect = view === 'columns' && hasProductFilter;
    const [selectedNodeKeys, setSelectedNodeKeys] = useState<string[]>([]);
    useEffect(() => {
        if (!canSelect) setSelectedNodeKeys([]);
    }, [canSelect]);

    // Zero out the (column, product) pairs selected in the tree. Selecting a column root cascades onto its
    // product leaves, so the selected keys already contain every targeted leaf; column roots are ignored by
    // the parser. The mutation skips pairs whose stock is ≤ 0.
    const handleZeroInventorySelection = useCallback(async () => {
        const columnProducts = selectedNodeKeys
            .map(parseColumnProductNodeId)
            .filter((pair): pair is { columnId: string; productId: string } => pair !== null);
        if (columnProducts.length === 0) return;

        try {
            const hasConfirmed = await confirmPopup({
                title: 'Confirmer',
                message: `Mettre à 0 le stock de ${columnProducts.length} produit(s) sélectionné(s) ?`,
            });
            if (!hasConfirmed) return;
            const { data } = await createZeroColumnInventoryStocks({ variables: { columnProducts } });
            const result = data?.createZeroColumnInventoryStocks;
            if (!result?.success) {
                toast.error(result?.error ?? "Une erreur est survenue lors de la création de l'inventaire des zéros");
                return;
            }
            toast.success('Inventaire des zéros créé avec succès');
            setSelectedNodeKeys([]);
            await refetchProductColumns();
        } catch {
            toast.error("Une erreur est survenue lors de la création de l'inventaire des zéros");
        }
    }, [selectedNodeKeys, createZeroColumnInventoryStocks, refetchProductColumns]);

    const actionSections = [
        {
            title: 'Actions',
            actions: [
                {
                    icon: <X />,
                    label: 'Tout désélectionner',
                    onClick: () => setSelectedNodeKeys([]),
                    hidden: !selectedNodeKeys.length,
                    dataTest: 'site-stocks-clear-selection',
                },
                {
                    icon: <CircleSlash />,
                    label: 'Inventaire des zéros',
                    requiredPermissions: [TotadminPermission.SiteInventoriesManage],
                    onClick: handleZeroInventorySelection,
                    hidden: !selectedNodeKeys.length,
                    dataTest: 'site-stocks-zero-inventory',
                },
            ],
        },
    ];

    const loadError = siteError || columnsError || productsError || productColumnsError;
    useEffect(() => {
        if (loadError) {
            toast.error('Une erreur est survenue lors de la récupération des stocks du site');
        }
    }, [loadError]);

    const columns = useMemo(() => getStockTreeColumns({ setInfos }), []);

    const rootNodes = useMemo<StockTreeNode[]>(() => {
        if (view === 'columns') {
            if (hasProductFilter) {
                const columnStocks = (productColumnsData?.siteProducts.siteProducts ?? []).flatMap(
                    ({ columnStocks: stocks }) => stocks,
                );
                return buildColumnsWithMatchingProducts(columnStocks);
            }
            return (columnsData?.columns ?? []).map(columnToNode);
        }
        return (productsData?.siteProducts.siteProducts ?? []).filter(({ stock }) => stock > 0).map(siteProductToNode);
    }, [view, hasProductFilter, columnsData, productsData, productColumnsData]);

    const loadChildren = useCallback(
        async (node: StockTreeNode): Promise<StockTreeNode[]> => {
            if (view === 'columns') {
                if (!node.columnId) return [];
                const { data } = await client.query<GetColumnProductStocksQuery, GetColumnProductStocksQueryVariables>({
                    query: GetColumnProductStocksDocument,
                    variables: { columnId: node.columnId },
                    fetchPolicy: 'cache-first',
                });
                return (data.column?.productStocks ?? []).map(productInColumnToNode);
            }

            if (!node.productId) return [];
            const { data } = await client.query<
                GetSiteProductColumnStocksQuery,
                GetSiteProductColumnStocksQueryVariables
            >({
                query: GetSiteProductColumnStocksDocument,
                variables: { siteId, productId: node.productId },
                fetchPolicy: 'cache-first',
            });
            return (data.siteProduct?.columnStocks ?? []).map(columnForProductToNode);
        },
        [view, client, siteId],
    );

    const switchView = useCallback((nextView: StockTreeView) => setView(nextView), []);

    if (siteLoading || columnsLoading) {
        return (
            <Container>
                <Loader mode={LoaderModeType.Spin} />
            </Container>
        );
    }

    if (siteError || !siteData || columnsError || !columnsData) {
        return null;
    }

    const site = siteData.site;
    const totem = site.totem;
    const organization = totem.organization;

    const isLoading =
        (view === 'products' && productsLoading) || (view === 'columns' && hasProductFilter && productColumnsLoading);

    const viewSwitcher = (
        <ViewSwitcher role="tablist">
            <ViewButton
                type="button"
                $active={view === 'columns'}
                onClick={() => switchView('columns')}
                data-test="stock-tree-view-columns"
            >
                Colonnes → Produits
            </ViewButton>
            <ViewButton
                type="button"
                $active={view === 'products'}
                onClick={() => switchView('products')}
                data-test="stock-tree-view-products"
            >
                Produits → Colonnes
            </ViewButton>
        </ViewSwitcher>
    );

    return (
        <Container>
            <PageHeader>
                <EntityPageBreadcrumb organization={organization} totem={totem} site={site} stocksView />
            </PageHeader>
            <ContentContainer>
                <Content>
                    {infos ? <VariationInfoPanel infos={infos} /> : null}
                    <ListContainer>
                        <DataTable<StockTreeNode, StockTreeFilter>
                            key={view}
                            columns={columns}
                            data={rootNodes}
                            filterMode="client"
                            getRowId={(row) => row.id}
                            getRowLink={(row) => row.detailPath}
                            getRowCanExpand={(row) => row.hasChildren}
                            loadChildren={loadChildren}
                            enableLazyTree
                            enableVirtualization
                            expanded={view === 'columns' && hasProductFilter ? true : undefined}
                            defaultSortBy="stock"
                            defaultSortOrder="desc"
                            isLoading={isLoading}
                            searchPlaceholder={view === 'columns' ? 'Nom de colonne' : 'Nom de produit'}
                            searchFilterKey="searchValue"
                            extraFilters={EXTRA_FILTERS}
                            onFiltersChange={handleFiltersChange}
                            toolbarActions={viewSwitcher}
                            enableSelection={canSelect}
                            selectedKeys={selectedNodeKeys}
                            onSelectionChange={setSelectedNodeKeys}
                        />
                    </ListContainer>
                </Content>
                {canSelect ? (
                    <ActionBar actionSections={actionSections} isInitiallyOpen={!!selectedNodeKeys.length} />
                ) : null}
            </ContentContainer>
        </Container>
    );
};

const VariationInfoPanel = ({ infos }: { infos: VariationInfo }) => (
    <InfoPanel style={{ backgroundColor: infos._bgColor }}>
        <div>
            <span>Jour de création : </span>
            <span>{new Date(infos.createdAt).toLocaleDateString()}</span>
        </div>
        <div>
            <span>Heure de création : </span>
            <span>{new Date(infos.createdAt).toLocaleTimeString()}</span>
        </div>
        <div>
            <span>Type : </span>
            <span>{infos.type}</span>
        </div>
        <div>
            <span>Valeur : </span>
            <span>{infos.valeur}</span>
        </div>
        <div>
            <span>Variation : </span>
            <span>{infos.variation}</span>
        </div>
        <div>
            <span>Explication : </span>
            <span>{infos.explication}</span>
        </div>
        <div>
            <span>Référence : </span>
            <span>{infos.reference}</span>
        </div>
    </InfoPanel>
);

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ContentContainer = styled.div`
    display: flex;
    flex: 1;
    overflow: hidden;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px;
    flex: 1;
    overflow: hidden;
`;

const ListContainer = styled.div`
    flex: 1;
    min-height: 0;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: var(--radius-lg);
    overflow: hidden;
`;

const ViewSwitcher = styled.div`
    display: inline-flex;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    overflow: hidden;
`;

const ViewButton = styled.button<{ $active: boolean }>`
    padding: 6px 12px;
    border: none;
    cursor: pointer;
    font-size: 13px;
    white-space: nowrap;
    background-color: ${({ $active, theme }) => ($active ? theme.ctaPrimaryColor : 'transparent')};
    color: ${({ $active, theme }) => ($active ? theme.ctaPrimaryTextColor : theme.textColor)};
`;

const InfoPanel = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    margin: 10px;
    padding: 10px;
    background: ${({ theme }) => theme.menuBackgroundColor};
    color: ${({ theme }) => theme.menuTextColor};
    z-index: 1000;
`;
